import { useQuery } from '@tanstack/react-query'
import { LogOut } from 'lucide-react'
import { Avatar } from '@/components/ui/Avatar'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { describeRoles } from '@/auth/profile'
import { useAuth, useProfile } from '@/auth/SessionProvider'
import { listDepartments } from '@/data/departments'

/** Who you are signed in as, what you can do, and how to sign out. */
export function AccountPage() {
  const profile = useProfile()
  const { signOut } = useAuth()
  const depts = useQuery({ queryKey: ['departments'], queryFn: () => listDepartments() })
  const nameOf = (id: string) => depts.data?.find((d) => d.id === id)?.name
  const roles = describeRoles(profile, nameOf)

  return (
    <div className="flex max-w-[600px] flex-col gap-6">
      <div className="flex items-center gap-4">
        <Avatar name={profile.fullName} size="lg" />
        <div className="min-w-0">
          <h2 className="truncate text-title font-semibold text-ink">Your account</h2>
          <p className="truncate text-body text-muted">
            {profile.fullName}
            {roles[0] ? ` · ${roles[0]}` : ''}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader title="Your details" />
        <CardBody>
          <dl className="flex flex-col gap-4">
            <div>
              <dt className="text-label font-medium text-muted">Name</dt>
              <dd className="mt-0.5 text-card text-ink">{profile.fullName}</dd>
            </div>
            <div>
              <dt className="text-label font-medium text-muted">Email</dt>
              <dd className="mt-0.5 break-all text-card text-ink">{profile.email}</dd>
            </div>
            <div>
              <dt className="text-label font-medium text-muted">Access</dt>
              <dd className="mt-1 flex flex-wrap gap-2">
                {roles.map((r) => (
                  <Badge key={r} tone="info">
                    {r}
                  </Badge>
                ))}
              </dd>
            </div>
          </dl>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Password" />
        <CardBody>
          <p className="text-body text-muted">To change your password, contact your administrator.</p>
        </CardBody>
      </Card>

      <div className="mt-4 border-t border-line pt-6">
        <Button variant="danger" onClick={() => void signOut()}>
          <LogOut size={16} aria-hidden /> Sign out
        </Button>
      </div>
    </div>
  )
}
